import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { GetTranscriptDataDocument } from '../../services/graphql/types/graphql';
import { apiClient } from '../../services/api/client';
import { Transcript } from '../Transcript';
import './styles.scss';

type EvaluationResult = {
  promptId: 'guided' | 'creative' | 'teacher';
  promptName: string;
  provider: 'qwen' | 'doubao' | 'glm';
  status: 'success' | 'error' | 'not_configured';
  model?: string;
  evaluation?: string;
  error?: string;
  durationMs: number;
  usage?: Record<string, number>;
};

type PromptVariant = { id: 'guided' | 'creative' | 'teacher'; name: string; content: string };

const providerNames = { qwen: '阿里 Qwen', doubao: '火山豆包', glm: '智谱 GLM' };

export const TranscriptTestPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const querySubmissionId = searchParams.get('submissionId')?.trim() ?? '';
  const [submissionId, setSubmissionId] = useState(querySubmissionId);
  const [requestedSubmissionId, setRequestedSubmissionId] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiPrompts, setAiPrompts] = useState<PromptVariant[]>([]);
  const [aiResults, setAiResults] = useState<EvaluationResult[]>([]);
  const [getTranscript, { data, loading, error, called }] = useLazyQuery(
    GetTranscriptDataDocument,
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (!querySubmissionId) return;
    setRequestedSubmissionId(querySubmissionId);
    setAiResults([]);
    void getTranscript({ variables: { submissionId: querySubmissionId } });
  }, [getTranscript, querySubmissionId]);

  const imageUrls = useMemo(() => data?.submission.artworks.flatMap(
    (artwork) => artwork.medias.map((media) => media.url.replace(/^http:/, 'https:')),
  ) ?? [], [data]);

  const rubrics = useMemo(() => data?.submission.assignment.assignmentRubrics.map((item) => ({
    name: item.rubric.name ?? '未命名评价维度',
    weight: item.weight,
    levels: item.rubric.rubricLevels.map((level) => ({ text: level.text, grade: level.grade })),
  })) ?? [], [data]);

  const teacherCredits = useMemo(() => data?.submission.credits.map((credit) => ({
    rubricName: credit.rubric.name,
    graderRoleType: credit.rubric.graderRoleType,
    levelText: credit.rubricLevel?.text,
    grade: credit.rubricLevel?.grade,
    comment: credit.comment,
  })) ?? [], [data]);

  const totalGrade = useMemo(() => {
    if (!data) return 0;
    const gradeByRubricId = new Map(
      data.submission.credits.map((credit) => [credit.rubric.rubricId, credit.rubricLevel?.grade ?? 0]),
    );
    return data.submission.assignment.assignmentRubrics.reduce(
      (total, item) => total + item.weight * (gradeByRubricId.get(item.rubric.rubricId) ?? 0),
      data.submission.gradeOffset ?? 0,
    );
  }, [data]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextId = submissionId.trim();
    if (!nextId) return;
    if (nextId === querySubmissionId) {
      setRequestedSubmissionId(nextId);
      setAiResults([]);
      void getTranscript({ variables: { submissionId: nextId } });
    } else setSearchParams({ submissionId: nextId });
  };

  const compareModels = async () => {
    if (!data || imageUrls.length === 0 || rubrics.length === 0) return;
    setAiLoading(true);
    setAiError('');
    setAiPrompts([]);
    setAiResults([]);
    try {
      const response = await apiClient.post<{ prompts: PromptVariant[]; results: EvaluationResult[] }>('/ai-evaluation/compare', {
        assignmentName: data.submission.assignment.name,
        assignmentDescription: data.submission.assignment.description,
        imageUrls,
        rubrics,
        teacherCredits,
        totalGrade,
        extraComment: data.submission.extraComment,
      });
      setAiResults(response.data.results);
      setAiPrompts(response.data.prompts);
    } catch (requestError: any) {
      setAiError(requestError.response?.data?.message ?? requestError.message ?? 'AI 评价请求失败');
    } finally {
      setAiLoading(false);
    }
  };

  return <main className="transcript-test-page">
    <section className="transcript-test-panel">
      <h1>成绩单与 AI 评价测试</h1>
      <p>输入 submissionId 获取作业图片和老师评价标准，再并行比较三个国产视觉模型。</p>
      <form className="transcript-test-form" onSubmit={handleSubmit}>
        <label htmlFor="transcript-test-submission-id">submissionId</label>
        <div className="transcript-test-form-row">
          <input id="transcript-test-submission-id" value={submissionId}
            onChange={(event) => setSubmissionId(event.target.value)}
            placeholder="请输入提交记录 ID" autoComplete="off" />
          <button type="submit" disabled={!submissionId.trim() || loading}>
            {loading ? '获取中…' : '获取成绩单'}
          </button>
        </div>
      </form>
      {error && <div className="transcript-test-error"><strong>成绩单请求失败</strong><pre>{error.message}</pre></div>}
      {called && !loading && !error && !data && <div className="transcript-test-empty">没有返回成绩单数据。</div>}
    </section>

    {data && requestedSubmissionId && <>
      <section className="transcript-test-preview">
        <h2>成绩单预览</h2>
        <Transcript submissionId={requestedSubmissionId} embedded />
      </section>
      <aside className="transcript-test-ai-pane">
      <section className="transcript-test-panel">
        <h2>多模型视觉评价</h2>
        <p>已提取 {imageUrls.length} 张作业图片、{rubrics.length} 个老师评价维度。</p>
        {imageUrls.length === 0 && <div className="transcript-test-empty">该提交没有可评价的图片。</div>}
        {rubrics.length === 0 && <div className="transcript-test-empty">该作业没有配置评价标准。</div>}
        <button className="transcript-test-ai-button" type="button" onClick={compareModels}
          disabled={aiLoading || imageUrls.length === 0 || rubrics.length === 0}>
          {aiLoading ? '三组共九次评价中…' : '用三份提示词同时测试三个模型'}
        </button>
        {aiError && <div className="transcript-test-error">{aiError}</div>}
      </section>
      {aiPrompts.map((prompt) => <section className="transcript-test-prompt-group" key={prompt.id}>
        <details className="transcript-test-prompt">
          <summary>查看{prompt.name}</summary>
          <pre>{prompt.content}</pre>
        </details>
        <h2 className="transcript-test-prompt-title">{prompt.name}的模型结果</h2>
        <section className="transcript-test-ai-grid">
          {aiResults.filter((result) => result.promptId === prompt.id).map((result) => (
            <article className={`transcript-test-ai-card is-${result.status}`} key={`${result.promptId}-${result.provider}`}>
              <header><h2>{providerNames[result.provider]}</h2><span>{result.durationMs} ms</span></header>
              {result.model && <div className="transcript-test-model">{result.model}</div>}
              {result.evaluation ? (
                <div className="transcript-test-markdown">
                  <ReactMarkdown>{result.evaluation}</ReactMarkdown>
                </div>
              ) : <div className="transcript-test-error">{result.error}</div>}
            </article>
          ))}
        </section>
      </section>)}
      </aside>
      <section className="transcript-test-response">
        <h2>GraphQL 原始响应</h2><pre>{JSON.stringify(data, null, 2)}</pre>
      </section>
    </>}
  </main>;
};
